/**
 * POS Layout - Touch-optimized layout for point of sale
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { posBase, posPath } from './posPath';
import { 
  ShoppingCart, 
  LayoutGrid, 
  ClipboardList,
  Banknote,
  Settings,
  LogOut,
  User,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from './context/AuthContext';

const navigation = [
  { name: 'POS', href: '', icon: ShoppingCart },
  // tables are the one floor (Restaurant OS): /dining/floor inside the POS
  { name: 'Tables', href: 'dining/floor', icon: LayoutGrid },
  { name: 'Orders', href: 'orders', icon: ClipboardList },
  { name: 'Drawer', href: 'drawer', icon: Banknote },
  { name: 'Settings', href: 'settings', icon: Settings },
];

export default function POSLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate(posPath('login'));
  };

  const isActive = (href) => {
    const basePath = posBase();
    const fullPath = href ? `${basePath}/${href}` : basePath;
    if (href === '') return location.pathname === basePath || location.pathname === `${basePath}/`;
    return location.pathname.startsWith(fullPath);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Navigation Bar - Hidden on mobile */}
      <aside className={cn(
        "fixed lg:relative inset-y-0 left-0 z-50 w-16 sm:w-20 bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-200",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="h-14 sm:h-20 flex items-center justify-center border-b border-slate-800">
          <img src="/chain29-logo.png" alt="Chain29" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 sm:py-4">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={posPath(item.href)}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex flex-col items-center justify-center py-2 sm:py-4 mx-1 sm:mx-2 rounded-lg sm:rounded-xl transition-colors",
                isActive(item.href)
                  ? "bg-emerald-600/20 text-emerald-400"
                  : "text-slate-500 hover:bg-slate-800 hover:text-white"
              )}
            >
              <item.icon className="w-5 h-5 sm:w-6 sm:h-6 mb-0.5 sm:mb-1" />
              <span className="text-[10px] sm:text-xs font-medium">{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* User section */}
        <div className="p-1 sm:p-2 border-t border-slate-800">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-full flex flex-col items-center justify-center py-2 sm:py-3 rounded-lg sm:rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-700 rounded-full flex items-center justify-center mb-0.5 sm:mb-1">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <span className="text-[10px] sm:text-xs truncate w-full text-center px-1">{user?.name?.split(' ')[0] || 'Staff'}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 relative lg:ml-0">
        {/* Mobile header bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 border-b border-slate-800 px-3 py-2 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            className="h-9 w-9 text-slate-400"
            onClick={() => setSidebarOpen(true)}
          >
            <LayoutGrid className="w-5 h-5" />
          </Button>
          <span className="text-white font-semibold flex-1">POS</span>
        </div>
        
        {/* Add padding top on mobile for fixed header */}
        <div className="lg:pt-0 pt-14">
          {children}
        </div>
      </main>

      {/* User menu overlay */}
      {showMenu && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed bottom-4 left-20 sm:left-24 z-50 bg-slate-800 rounded-xl border border-slate-700 shadow-2xl overflow-hidden">
            <div className="p-3 sm:p-4 border-b border-slate-700">
              <p className="text-white font-medium text-sm sm:text-base">{user?.name || 'Staff User'}</p>
              <p className="text-xs sm:text-sm text-slate-400">{user?.role || 'Cashier'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-red-400 hover:bg-slate-700 transition-colors text-sm"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
